/* Devocione — visible-only, muted original clips. Inline display only; no card links or modal player. */
(() => {
  'use strict';
  if (customElements.get('dv7-community')) return;
  class Community extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController(); const options = { signal: this.controller.signal };
      this.rail = this.querySelector('[data-dv7-rail]'); if (!this.rail) return;
      this.cards = [...this.querySelectorAll('[data-dv7-card]')];
      this.previews = [...this.querySelectorAll('[data-dv7-preview]')];
      this.previous = this.querySelector('[data-dv7-prev]'); this.next = this.querySelector('[data-dv7-next]');
      this.count = this.querySelector('[data-dv7-count]'); this.toggle = this.querySelector('[data-dv7-preview-toggle]');
      this.motion = matchMedia('(prefers-reduced-motion:reduce)'); this.previewPaused = false; this.explicitPlay = false;
      this.visiblePreviews = new Set();
      this.querySelector('[data-dv7-rail-buttons]')?.removeAttribute('hidden');
      if (this.toggle) { this.toggle.hidden = !this.previews.length; this.toggle.addEventListener('click', () => {
        const playing = this.previews.some(video => !video.paused);
        this.previewPaused = playing; this.explicitPlay = !playing; this.refreshPreviews();
      }, options); }
      const move = direction => {
        const card = this.cards[0]; if (!card) return;
        const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
        this.rail.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: this.motion.matches ? 'instant' : 'smooth' });
      };
      this.previous?.addEventListener('click', () => move(-1), options);
      this.next?.addEventListener('click', () => move(1), options);
      this.rail.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame); this.scrollFrame = requestAnimationFrame(() => this.refresh());
      }, { ...options, passive: true });
      this.rail.addEventListener('keydown', event => {
        if (event.target !== this.rail) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1); }
        if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); this.rail.scrollTo({ left: event.key === 'Home' ? 0 : this.rail.scrollWidth, behavior: 'instant' }); }
      }, options);
      if ('ResizeObserver' in window) { this.resizeObserver = new ResizeObserver(() => this.refresh()); this.resizeObserver.observe(this.rail); }
      this.previews.forEach(video => {
        video.muted = true; video.defaultMuted = true;
        video.addEventListener('playing', () => { delete video.dataset.autoplayBlocked; video.classList.add('is-playing'); this.refreshToggle(); }, options);
        video.addEventListener('pause', () => this.refreshToggle(), options);
        video.addEventListener('error', () => { video.classList.remove('is-playing'); video.dataset.failed = 'true'; }, options);
      });
      if ('IntersectionObserver' in window) {
        this.videoObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => { if (entry.isIntersecting && entry.intersectionRatio >= 0.25) this.visiblePreviews.add(entry.target); else this.visiblePreviews.delete(entry.target); });
          this.refreshPreviews();
        }, { threshold: [0, 0.25, 0.6] });
        this.previews.forEach(video => this.videoObserver.observe(video));
      } else {
        // Progressive fallback: a first visible card only, never download the entire rail.
        if (this.previews[0]) this.visiblePreviews.add(this.previews[0]);
      }
      document.addEventListener('visibilitychange', () => this.refreshPreviews(), options);
      document.addEventListener('dv7:video:state', () => this.refreshPreviews(), options);
      this.motion.addEventListener('change', () => { this.explicitPlay = false; this.refreshPreviews(); }, options);
      document.addEventListener('shopify:block:select', event => {
        const card = this.cards.find(item => item.dataset.blockId === event.detail?.blockId);
        if (card) this.rail.scrollTo({ left: card.offsetLeft - this.cards[0].offsetLeft, behavior: 'instant' });
      }, options);
      this.refresh(); this.refreshPreviews();
    }
    refresh() {
      const box = this.rail.getBoundingClientRect();
      const visible = this.cards.map((card, i) => ({ i, box: card.getBoundingClientRect() })).filter(item => item.box.right > box.left + 12 && item.box.left < box.right - 12);
      if (this.previous) this.previous.disabled = this.rail.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - 3;
      if (this.count) {
        const first = visible[0]?.i, last = visible.at(-1)?.i;
        this.count.textContent = visible.length ? `${first + 1}${last === first ? '' : `–${last + 1}`} / ${this.cards.length}` : '';
      }
    }
    refreshToggle() {
      if (!this.toggle) return;
      const visible = [...this.visiblePreviews].filter(video => !video.dataset.failed);
      const blocked = visible.length > 0 && visible.every(video => video.paused && video.dataset.autoplayBlocked === 'true');
      const paused = this.previewPaused || blocked || (!this.explicitPlay && (this.motion.matches || this.dataset.autoplayPreviews === 'false'));
      this.toggle.textContent = paused ? 'Reproducir videos' : 'Pausar videos'; this.toggle.setAttribute('aria-pressed', String(paused));
    }
    refreshPreviews() {
      const shouldPlay = !this.previewPaused && (this.explicitPlay || (this.dataset.autoplayPreviews !== 'false' && !this.motion.matches)) && !document.hidden && !document.querySelector('[data-dv7-video-dialog][open]');
      this.previews.forEach(video => {
        if (!shouldPlay || !this.visiblePreviews.has(video) || video.dataset.failed) { video.pause(); return; }
        if (!video.dataset.loaded) {
          video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
          video.dataset.loaded = 'true'; video.preload = 'metadata'; video.load();
        }
        if (video.paused) {
          video.muted = true;
          video.play()?.catch(error => {
            if (error.name === 'NotAllowedError') video.dataset.autoplayBlocked = 'true';
            // Keep the poster or last frame. Retry with the existing global button, never a card overlay.
            this.refreshToggle();
          });
        }
      });
      this.refreshToggle();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null; this.resizeObserver?.disconnect(); this.videoObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame); this.previews?.forEach(video => video.pause());
    }
  }
  customElements.define('dv7-community', Community);
})();
